import {useContext, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {list as cancellationReasonList} from "api/cancellationReason/list";
import {couponCodesList} from "api/order/couponCodes";
import {list as lineItemShippingMethodsList} from "api/lineItem/shippingMethods/list";

import {OrderListContext} from "../context/OrderListContext";

import {getTranslation} from "domain/helpers/translations";

import {cachedRequest, ORG_REFERENCE_TTL_MS, FACET_TTL_MS} from "utils/referenceDataCache";
import {getAllStatus} from "utils/LineItemStatus";

function toFilterOption(type, id, name) {
    return {"@type": type, "id": id, "name": name};
}

FilterOptionsSync.propTypes = {
    currentOrganization: PropTypes.object,
};

function FilterOptionsSync({currentOrganization}) {
    const {setFilters, setOrganizationCouponCodes} = useContext(OrderListContext);

    const [organizationLineItemStatus, setOrganizationLineItemStatus] = useState([]);
    const [organizationCancellationReasons, setOrganizationCancellationReasons] = useState([]);
    const [organizationCouponCodes, setLocalOrganizationCouponCodes] = useState([]);
    const [organizationShippingMethods, setOrganizationShippingMethods] = useState([]);

    useEffect(() => {
        setOrganizationLineItemStatus(getAllStatus());

        cachedRequest('cancellationReasons', '', ORG_REFERENCE_TTL_MS, () => cancellationReasonList()).then((res) => {
            const reasons = [];
            const alreadyListedReasonNames = [];

            res.forEach(cancellationReason => {
                if (alreadyListedReasonNames.indexOf(getTranslation(cancellationReason).name) < 0) {
                    reasons.push(cancellationReason);
                    alreadyListedReasonNames.push(getTranslation(cancellationReason).name);
                }
            });

            setOrganizationCancellationReasons(reasons.sort((a, b) => {
                return getTranslation(a).name.localeCompare(getTranslation(b).name);
            }));
        });

        cachedRequest('couponCodes', '', FACET_TTL_MS, () => couponCodesList())
            .then((res) => setLocalOrganizationCouponCodes(res ?? []))
            .catch(() => setLocalOrganizationCouponCodes([]));

        cachedRequest('lineItemShippingMethods', '', ORG_REFERENCE_TTL_MS, () => lineItemShippingMethodsList())
            .then((res) => setOrganizationShippingMethods(res ?? []))
            .catch(() => setOrganizationShippingMethods([]));
    }, []);

    useEffect(() => {
        const builtFilters = {};

        if (organizationLineItemStatus.length > 0) {
            builtFilters.LineItemStatus = organizationLineItemStatus.map((lineItemStatus) =>
                toFilterOption("LineItemStatus", lineItemStatus, <FormattedMessage id={"picking.order.lineItemStatus." + lineItemStatus} />)
            );
        }

        if (organizationCancellationReasons.length > 0) {
            builtFilters.CancellationReason = organizationCancellationReasons.map((cancellationReason) => {
                const name = getTranslation(cancellationReason).name;

                return toFilterOption("CancellationReason", name, name);
            });
        }

        if (organizationShippingMethods.length > 0) {
            builtFilters.ShippingMethod = organizationShippingMethods.map((shippingMethod) =>
                toFilterOption(
                    "ShippingMethod",
                    shippingMethod.id,
                    shippingMethod.name ?? <FormattedMessage id={"picking.order.shippingMethod." + shippingMethod.shippingMethod} />
                )
            );
        }

        if (organizationCouponCodes.length > 0) {
            builtFilters.CouponCode = organizationCouponCodes.map((code) => toFilterOption("CouponCode", code, code));
        }

        if (currentOrganization && currentOrganization.sellerFilter) {
            builtFilters.SellerId = [];
        }

        if (currentOrganization?.inventories?.length > 1) {
            builtFilters.Inventory = currentOrganization.inventories.map((inventory) =>
                // same fallback chain as Inventory::getInventoryCenterName, shown on the zone badges
                toFilterOption("Inventory", inventory.id, inventory.inventoryAddress?.name || getTranslation(inventory)?.name || inventory.code)
            );
        }

        builtFilters.ReceiptValue = [];

        setFilters(builtFilters);
        setOrganizationCouponCodes(organizationCouponCodes);
    }, [
        organizationLineItemStatus,
        organizationCancellationReasons,
        organizationShippingMethods,
        organizationCouponCodes,
        currentOrganization,
        setFilters,
        setOrganizationCouponCodes
    ]);

    return null;
}

const mapStateToProps = state => ({
    currentOrganization: state.currentOrganization.retrieved ?? null,
});

export default connect(mapStateToProps)(FilterOptionsSync);
